export const ACTIVITY_COOKIE_NAME = 'tai-last-activity';

/**
 * Employee-pool idle stamp, kept in a separate cookie from the customer one.
 *
 * TR-3393: idle enforcement used to be skipped entirely for employee-pool requests
 * because a single path=/ cookie was shared by both pools — enforcing on employee
 * traffic would 401 a fresh employee session whose customer stamp had gone stale in
 * another tab, and employee activity would reset the customer's clock. Splitting the
 * stamp per pool removes the interference, which is what lets both pools be enforced
 * on the same 30-minute policy with no exemption.
 */
export const EMPLOYEE_ACTIVITY_COOKIE_NAME = 'tai-last-activity-employee';

/**
 * TR-1839: host-only marker that `/api/init` sets when it sends one user to another
 * cell. The value is `<sha256(sub)[:16]>:<destination cell>`, so a second WRONG_CELL
 * for the same user and destination inside the TTL answers Access Denied instead of
 * another redirect. A different user or destination never matches.
 */
export const WRONG_CELL_HOP_COOKIE_NAME = 'tai-wrong-cell-hop';
export const WRONG_CELL_HOP_TTL_SECONDS = 120;

export const ACTIVITY_HEADER = 'x-tai-user-activity';
export const IDLE_TIMEOUT_HEADER = 'X-Auth-Idle-Timeout';

/**
 * Which pool's idle clock the sending UI belongs to, so `/api/session/extend`
 * stamps the right one.
 *
 * Needed because the pool is otherwise resolved by cookie precedence
 * (customer first, employee as fallback) rather than by route. A CSM holds cookies
 * for both pools, so every heartbeat and every "Stay signed in" from
 * `/employee/app` resolved to `'customer'` and refreshed the customer clock —
 * leaving `tai-last-activity-employee` frozen at whatever it was when the page
 * loaded. `/api/employee/init` only ever *preserves* that stamp, so the next
 * reload or tenant switch idle-401'd a session that had been in continuous use.
 *
 * Deliberately NOT {@link AUTH_POOL_HEADER}: that header is set by the proxy and
 * downstream routes trust it to pick an ID token. This one is a client hint and is
 * only ever used to choose which of the caller's *own* sessions to read, so a
 * forged value can at worst stamp a clock the sender already owns.
 */
export const SESSION_POOL_HEADER = 'x-tai-session-pool';

export const SESSION_ACTIVITY_EVENTS = [
    'mousedown',
    'keydown',
    'touchstart',
    'scroll',
    'mousemove',
    'tp-media-playing',
] as const;

/** Coalesce high-frequency activity signals before React state and tab sync. */
export const ACTIVITY_EMIT_THROTTLE_MS = 10_000;
